/**
 * Admin Role Permissions Configuration (Frontend Mirror)
 *
 * This mirrors the AUTHORITATIVE backend config at
 * `backend/config/adminPermissions.js`. The backend is the single source of
 * truth: a player's effective permissions are computed server-side and sent
 * to the client; these constants only exist so the UI can reference permission
 * strings for show/hide gating.
 *
 * DRIFT RISK: this table is hand-copied. The `ADMIN_ROLES` and `PERMISSIONS`
 * objects MUST stay byte-for-byte in sync with the backend. A Vitest guard
 * (`adminPermissions.drift.test.js`) imports the backend module and asserts
 * equality, so an out-of-sync edit fails the test suite. Update both files
 * together.
 *
 * Roles: super_admin, marketing, support, finance
 */

// Valid admin roles
export const ADMIN_ROLES = {
  SUPER_ADMIN: "super_admin",
  MARKETING: "marketing",
  SUPPORT: "support",
  FINANCE: "finance",
};

// Permission categories - mirrors backend/config/adminPermissions.js
export const PERMISSIONS = {
  // Full access
  ALL: "*",

  // Dashboard & Analytics
  DASHBOARD_READ: "dashboard:read",
  ANALYTICS_READ: "analytics:read",
  REPORTS_READ: "reports:read",

  // Player Management
  PLAYERS_READ: "players:read",
  PLAYERS_WRITE: "players:write",
  PLAYERS_BALANCE: "players:balance",
  PLAYERS_SUSPEND: "players:suspend",

  // Transactions
  TRANSACTIONS_READ: "transactions:read",
  TRANSACTIONS_WRITE: "transactions:write",

  // Redemptions
  REDEMPTIONS_READ: "redemptions:read",
  REDEMPTIONS_PROCESS: "redemptions:process",

  // Offers/Commerce
  OFFERS_READ: "offers:read",
  OFFERS_WRITE: "offers:write",

  // Bonuses (wheels, plans, etc.)
  BONUSES_READ: "bonuses:read",
  BONUSES_WRITE: "bonuses:write",

  // Configuration
  SITE_CONFIG_READ: "site_config:read",
  SITE_CONFIG_WRITE: "site_config:write",
  PAYMENT_CONFIG_READ: "payment_config:read",
  PAYMENT_CONFIG_WRITE: "payment_config:write",

  // Admin Management
  ADMIN_ROLES_READ: "admin_roles:read",
  ADMIN_ROLES_WRITE: "admin_roles:write",

  // Affiliates
  AFFILIATES_READ: "affiliates:read",
  AFFILIATES_WRITE: "affiliates:write",

  // Games
  GAMES_READ: "games:read",
  GAMES_WRITE: "games:write",

  // VIP tiers (daily reward economics, a Protected Financial Parameter)
  VIP_READ: "vip:read",
  VIP_WRITE: "vip:write",

  // Notifications (player comms: types, sends, templates)
  NOTIFICATIONS_READ: "notifications:read",
  NOTIFICATIONS_WRITE: "notifications:write",

  // Source codes (marketing campaign attribution)
  SOURCE_CODES_READ: "source_codes:read",
  SOURCE_CODES_WRITE: "source_codes:write",

  // Card disputes (financial dispute case management, can suspend players)
  DISPUTES_READ: "disputes:read",
  DISPUTES_WRITE: "disputes:write",

  // AMOE (legally-required free-entry queue, credits CB)
  AMOE_READ: "amoe:read",
  AMOE_PROCESS: "amoe:process",

  // Support Tickets (Zendesk access)
  SUPPORT_TICKETS_READ: "support_tickets:read",

  // A/B Experiments (Growth Hub: create/manage experiments + read report cards)
  EXPERIMENTS_READ: "experiments:read",
  EXPERIMENTS_WRITE: "experiments:write",
};

/**
 * Check if a permissions array includes a specific permission
 * Supports wildcard '*' for full access
 * @param {string[]} permissions - User's permissions array
 * @param {string|string[]} requiredPermission - Required permission(s)
 * @returns {boolean} Whether user has the required permission
 */
export function hasPermission(permissions, requiredPermission) {
  if (!permissions || !Array.isArray(permissions)) return false;

  // Wildcard grants all permissions
  if (permissions.includes("*")) return true;

  // Check if any required permission is granted
  const required = Array.isArray(requiredPermission)
    ? requiredPermission
    : [requiredPermission];
  return required.some((perm) => permissions.includes(perm));
}

/**
 * Get display name for a role
 * @param {string} role - Admin role
 * @returns {string} Human-readable role name
 */
export function getRoleDisplayName(role) {
  if (!role) return "Unknown";

  const displayNames = {
    [ADMIN_ROLES.SUPER_ADMIN]: "Super Admin",
    [ADMIN_ROLES.MARKETING]: "Marketing",
    [ADMIN_ROLES.SUPPORT]: "Support",
    [ADMIN_ROLES.FINANCE]: "Finance",
  };
  return displayNames[role] || role;
}

export default {
  ADMIN_ROLES,
  PERMISSIONS,
  hasPermission,
  getRoleDisplayName,
};
